import { useEffect, useState } from "react";
import { StatusChip, ScreenLoading, Warn } from "../components";
import { WORK_CATEGORIES, WORK_SUBCATEGORIES, api, type Dashboard as Dash, type ReferenceGroup } from "../lib/api";
import { STATUS as S, fmtDateTime, relTime } from "../lib/status";
import { WorkCategoryFiles } from "./WorkCategoryFiles";

/**
 * The management view.
 *
 * Read-only by capability, not by hiding buttons: management holds area.read and
 * nothing else, so this page cannot even fetch an evidence image. That is the
 * point of capabilities rather than a role ladder (ADR 0004) — the restriction
 * is on the server, so it does not depend on this component being correct.
 */

/**
 * One region-table cell (#53): the area count with its izin-status split as a
 * stacked bar, after the kecamatan-table precedent. Exact numbers ride the
 * title, so a tap shows them.
 */
function RegionCell({ cell }: { cell: { total: number; permitted: number; in_progress: number; blocked: number } }) {
  return (
    <div title={`Diizinkan ${cell.permitted}, In Progress ${cell.in_progress}, Tertolak ${cell.blocked}`}>
      <div className="mono">{cell.total}</div>
      {cell.total > 0 && (
        <div className="stacked" style={{ width: "100%" }}>
          <div style={{ width: `${(cell.permitted / cell.total) * 100}%`, background: "var(--permitted)" }} title={`Diizinkan ${cell.permitted}`} />
          <div style={{ width: `${(cell.in_progress / cell.total) * 100}%`, background: "var(--in-progress)" }} title={`In Progress ${cell.in_progress}`} />
          <div style={{ width: `${(cell.blocked / cell.total) * 100}%`, background: "var(--blocked)" }} title={`Tertolak ${cell.blocked}`} />
        </div>
      )}
    </div>
  );
}
export function Dashboard({ onOpen, canOpen, canUpload }: { onOpen: (areaId: number) => void; canOpen: boolean; canUpload: boolean }) {
  const [d, setD] = useState<Dash | null>(null);
  const [refs, setRefs] = useState<ReferenceGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // #53: the category view is the page as it was; the region view swaps the
  // work sections for the GM -> AREA table. Global totals, the kecamatan
  // table, recent activity, and the legend stay above and below both views.
  const [view, setView] = useState<"kategori" | "wilayah">("kategori");
  // #54: one filter bar above both views -- GM -> AREA cascade plus a
  // work-category dropdown -- narrowing rows in both views. All filtering
  // is client-side narrowing of the server-computed payload: no new
  // endpoints, no re-derivation. The dashboard payload already carries
  // every count; this component only hides rows and sections.
  const [gmFilter, setGmFilter] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [workFilter, setWorkFilter] = useState("");

  useEffect(() => {
    let live = true;
    const load = () => {
      void api
        .dashboard()
        .then((r) => live && setD(r))
        .catch((e) => live && setError(e instanceof Error ? e.message : "Gagal memuat dasbor."));
      void api
        .references()
        .then((r) => live && setRefs(r))
        .catch((e) => live && setError(e instanceof Error ? e.message : "Gagal memuat berkas referensi."));
    };
    load();
    const t = setInterval(load, 30_000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, []);

  if (error) return <div className="page"><Warn tone="red">{error}</Warn></div>;
  if (!d) return <div className="page"><ScreenLoading label="Memuat dashboard…" /></div>;

  const pct = d.required ? Math.round((d.granted / d.required) * 100) : 0;
  const maxKec = Math.max(1, ...d.by_kecamatan.map((k) => k.total));

  // #54 derivation (all client-side, all from the payload above).
  //
  // AREA identity: Y is the count of operational AREA rows the server
  // computed (one per AREA holding areas); Belum dipetakan is not an AREA
  // and never counts. X is the same count after the three filters. The
  // line Menampilkan X dari Y AREA therefore names exactly the rows the
  // region view can show, so a filtered-out AREA never vanishes silently.
  const totalAreas = d.region_sections.reduce((n, g) => n + g.areas.length, 0);
  const gmOptions = d.region_sections.map((g) => g.gm);
  const areaOptions = gmFilter
    ? (d.region_sections.find((g) => g.gm === gmFilter)?.areas.map((a) => a.area) ?? [])
    : d.region_sections.flatMap((g) => g.areas.map((a) => a.area));
  const workOptions = Object.keys(WORK_CATEGORIES);
  const subsOf = (w: string): readonly string[] =>
    (WORK_CATEGORIES as Record<string, readonly string[]>)[w] ?? [];
  const rowMatchesWork = (bySub: { work_subcategory: string; total: number }[], w: string): boolean => {
    if (!w) return true;
    const subs = new Set(subsOf(w));
    return bySub.some((c) => subs.has(c.work_subcategory) && c.total > 0);
  };
  // Region view: keep only the GM section(s) and AREA row(s) selected, and
  // only rows holding the selected work parent. GM headers re-sum over the
  // kept rows so the header count narrows with the rows. Rows whose areas
  // carry no work pair (by_subcategory empty) match no work filter and
  // stand down -- visibly filtered, never silently kept.
  const filteredRegions = d.region_sections
    .filter((g) => !gmFilter || g.gm === gmFilter)
    .map((g) => {
      const areas = g.areas.filter(
        (a) => (!areaFilter || a.area === areaFilter) && rowMatchesWork(a.by_subcategory, workFilter),
      );
      return {
        ...g,
        areas,
        total: areas.reduce((n, r) => n + r.total, 0),
        permitted: areas.reduce((n, r) => n + r.permitted, 0),
        in_progress: areas.reduce((n, r) => n + r.in_progress, 0),
        blocked: areas.reduce((n, r) => n + r.blocked, 0),
      };
    })
    .filter((g) => g.areas.length > 0);
  const shownAreas = filteredRegions.reduce((n, g) => n + g.areas.length, 0);
  const isFiltered = gmFilter !== "" || areaFilter !== "" || workFilter !== "";
  // Belum dipetakan has no GM or AREA, so any region filter stands it
  // down; a work filter keeps it only where its own subs match. An empty
  // unmapped bucket renders (with its honest empty note) only unfiltered.
  const showUnmapped =
    d.unmapped_region.total === 0
      ? !isFiltered
    : !gmFilter && !areaFilter && rowMatchesWork(d.unmapped_region.by_subcategory, workFilter);
  // Category view: a work filter keeps only its own section; a region
  // filter keeps a section only where a matching AREA row holds that
  // section's work parent. Inner KPIs stay the server-computed per-parent
  // counts -- the filter hides whole sections, it never re-derives them.
  const visibleWorkSections = d.work_sections.filter((s) => {
    if (workFilter && s.work_category !== workFilter) return false;
    if (gmFilter || areaFilter) {
      const matching = d.region_sections
        .filter((g) => !gmFilter || g.gm === gmFilter)
        .flatMap((g) => g.areas)
        .filter((a) => !areaFilter || a.area === areaFilter);
      const subs = new Set(subsOf(s.work_category));
      return matching.some((a) =>
        a.by_subcategory.some((c) => subs.has(c.work_subcategory) && c.total > 0),
      );
    }
    return true;
  });

  return (
    <div className="page">
      <h1 className="page-title">Dashboard</h1>
      <p className="page-sub">Ringkasan izin lokasi dan deployment. Diperbarui tiap 30 detik.</p>

      <div className="kpis">
        <div className="kpi">
          <div className="kpi-n" style={{ color: "var(--permitted)" }}>{d.totals.permitted}</div>
          <div className="kpi-l">Area terizin</div>
        </div>
        <div className="kpi">
          <div className="kpi-n" style={{ color: "var(--in-progress)" }}>{d.totals.in_progress}</div>
          <div className="kpi-l">Area dalam proses</div>
        </div>
        <div className="kpi">
          <div className="kpi-n" style={{ color: "var(--blocked)" }}>{d.totals.blocked}</div>
          <div className="kpi-l">Area ditolak</div>
        </div>
        <div className="kpi">
          <div className="kpi-n">{d.areas}</div>
          <div className="kpi-l">Total area</div>
        </div>
        <div className="kpi">
          <div className="kpi-n">{pct}%</div>
          <div className="kpi-l">
            Persetujuan terpenuhi ({d.granted}/{d.required})
          </div>
        </div>
      </div>

      {d.pending_evidence > 0 && (
        <div className="mb16">
          <Warn tone="amber">
            {d.pending_evidence} bukti belum selesai terunggah di perangkat yang merekamnya. Keputusan sudah
            tercatat; foto menyusul bila koneksi tersedia.
          </Warn>
        </div>
      )}

      <div className="panel">
        <h3>Persetujuan per kecamatan</h3>
        <table>
          <thead>
            <tr>
              <th style={{ width: "34%" }}>Kecamatan</th>
              <th style={{ width: "44%" }}>Sebaran status</th>
              <th style={{ textAlign: "right" }}>Area</th>
            </tr>
          </thead>
          <tbody>
            {d.by_kecamatan.map((k) => (
              <tr key={k.kecamatan}>
                <td>{k.kecamatan}</td>
                <td>
                  <div className="stacked" style={{ width: `${Math.max(14, (k.total / maxKec) * 100)}%` }}>
                    <div style={{ width: `${(k.permitted / k.total) * 100}%`, background: "var(--permitted)" }} title={`Diizinkan ${k.permitted}`} />
                    <div style={{ width: `${(k.in_progress / k.total) * 100}%`, background: "var(--in-progress)" }} title={`In Progress ${k.in_progress}`} />
                    <div style={{ width: `${(k.blocked / k.total) * 100}%`, background: "var(--blocked)" }} title={`Ditolak ${k.blocked}`} />
                  </div>
                </td>
                <td style={{ textAlign: "right" }} className="mono">
                  {k.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="rowflex wrap mt8 small mute">
          {(["permitted", "in_progress", "blocked"] as const).map((s) => (
            <span className="rowflex" key={s}>
              <span className={`legend-swatch sw-${s}`} style={{ width: 14, height: 8 }} />
              {S[s].label}
            </span>
          ))}
        </div>
      </div>

      {/* Shared filter bar (#54): one bar above both views -- GM -> AREA
          cascade plus work-category dropdown. AREA options follow the
          selected GM; picking a GM clears an AREA that GM does not hold.
          The Menampilkan line always names the kept rows out of the
          server-computed total, and the clear control shows only while
          filtered; clearing restores both views whole. */}
      <div className="panel" aria-label="Filter dasbor" style={{ margin: "16px 0" }}>
        <div className="rowflex wrap" style={{ gap: 8 }}>
          <label className="small">GM{" "}
            <select
              id="filter-gm"
              aria-label="Filter GM"
              value={gmFilter}
              onChange={(e) => {
                const v = e.target.value;
                setGmFilter(v);
                if (v) {
                  const allowed = d.region_sections.find((g) => g.gm === v)?.areas.map((a) => a.area) ?? [];
                  if (areaFilter && !allowed.includes(areaFilter)) setAreaFilter("");
                }
              }}
            >
              <option value="">Semua GM</option>
              {gmOptions.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </label>
          <label className="small">AREA{" "}
            <select
              id="filter-area"
              aria-label="Filter AREA"
              value={areaFilter}
              onChange={(e) => setAreaFilter(e.target.value)}
            >
              <option value="">Semua AREA</option>
              {areaOptions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </label>
          <label className="small">Kategori{" "}
            <select
              id="filter-work"
              aria-label="Filter kategori pekerjaan"
              value={workFilter}
              onChange={(e) => setWorkFilter(e.target.value)}
            >
              <option value="">Semua kategori</option>
              {workOptions.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="rowflex wrap mt8" style={{ gap: 8, alignItems: "center" }}>
          <span id="filter-count" className="small mute">Menampilkan {shownAreas} dari {totalAreas} AREA</span>
          {isFiltered && (
            <button type="button" id="filter-clear" className="btn btn-sm btn-ghost" onClick={() => { setGmFilter(""); setAreaFilter(""); setWorkFilter(""); }}>
              Hapus filter
            </button>
          )}
        </div>
      </div>

      <div className="rowflex" role="group" aria-label="Tampilan dasbor" style={{ margin: "16px 0" }}>
        <button
          type="button"
          className={view === "kategori" ? "btn btn-sm" : "btn btn-sm btn-ghost"}
          aria-pressed={view === "kategori"}
          onClick={() => setView("kategori")}
        >
          Kategori
        </button>
        <button
          type="button"
          className={view === "wilayah" ? "btn btn-sm" : "btn btn-sm btn-ghost"}
          aria-pressed={view === "wilayah"}
          onClick={() => setView("wilayah")}
        >
          Wilayah
        </button>
      </div>

      {/* Region view (#53): AREA rows under GM group headers, one column per
          work sub, cells with area counts split by izin status -- all computed
          server-side in the dashboard derivation from each area's stored
          kabupaten_kota. No file lists here: references live in the category
          view. Areas whose kabupaten maps nowhere read in Belum dipetakan,
          never silently dropped. */}
      {view === "wilayah" && (
        <>
          {filteredRegions.map((g) => (
            <section key={g.gm} aria-label={`Wilayah ${g.gm}`}>
              <h3 style={{ margin: "20px 0 8px" }}>
                {g.gm} <span className="small mute">· {g.total} area</span>
              </h3>
              <div className="panel">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: "22%" }}>AREA</th>
                      {WORK_SUBCATEGORIES.map((sub) => (
                        <th key={sub}>{sub}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {g.areas.map((r) => (
                      <tr key={r.area}>
                        <td>{r.area}</td>
                        {WORK_SUBCATEGORIES.map((sub) => {
                          const c = r.by_subcategory.find((x) => x.work_subcategory === sub) ??
                            { total: 0, permitted: 0, in_progress: 0, blocked: 0 };
                          return (
                            <td key={sub}>
                              <RegionCell cell={c} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
          {showUnmapped && (
          <section aria-label="Wilayah belum dipetakan">
            <h3 style={{ margin: "20px 0 8px" }}>
              Belum dipetakan <span className="small mute">· {d.unmapped_region.total} area</span>
            </h3>
            <div className="panel">
              {d.unmapped_region.total === 0 ? (
                <p className="mute small" style={{ margin: 0 }}>
                  Semua area sudah terpetakan ke AREA operasionalnya.
                </p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: "22%" }}>Kabupaten/kota tak terpetakan</th>
                      {WORK_SUBCATEGORIES.map((sub) => (
                        <th key={sub}>{sub}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Belum dipetakan</td>
                      {WORK_SUBCATEGORIES.map((sub) => {
                        const c = d.unmapped_region.by_subcategory.find((x) => x.work_subcategory === sub) ??
                          { total: 0, permitted: 0, in_progress: 0, blocked: 0 };
                        return (
                          <td key={sub}>
                            <RegionCell cell={c} />
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </section>
          )}
        </>
      )}

      {/* Sections slice (#50): one section per work parent with its own KPIs
          and kecamatan breakdown, both computed server-side and rendered here
          without derivation. Headers follow the Database two-row pattern:
          the parent, with its subs beneath in smaller text. Each section nests
          its reference file list (#51, bare: no second section+h2) so the
          page holds three headers, not six. Recent activity stays one global
          list below; global totals stay above. */}
      {view === "kategori" && (
      <>
      {refs && refs.length > 0 && (
        <div className="panel">
          <h3>Berkas referensi per kategori pekerjaan</h3>
          <p className="small mute" style={{ marginTop: 0 }}>
            Bacaan latar untuk tiap kategori pekerjaan. Referensi saja: tidak membuktikan
            apa-apa dan tidak mengubah status area mana pun.
          </p>
        </div>
      )}
      {visibleWorkSections.map((s) => {
        const secPct = s.required ? Math.round((s.granted / s.required) * 100) : 0;
        const secMax = Math.max(1, ...s.by_kecamatan.map((k) => k.total));
        return (
          <section key={s.work_category} aria-label={`Kategori ${s.work_category}`}>
            <h2 className="page-sub" style={{ fontSize: 16, margin: "20px 0 8px", lineHeight: 1.35 }}>
              {s.work_category}
              <br />
              <span className="small mute">{s.subcategories.join(", ")}</span>
            </h2>
            {s.total === 0 ? (
              <div className="panel">
                <p className="mute small" style={{ margin: 0 }}>
                  Belum ada area {s.work_category} di kecamatan mana pun.
                </p>
              </div>
            ) : (
              <>
                <div className="kpis">
                  <div className="kpi">
                    <div className="kpi-n">{s.total}</div>
                    <div className="kpi-l">Total area {s.work_category}</div>
                  </div>
                  <div className="kpi">
                    <div className="kpi-n" style={{ color: "var(--permitted)" }}>{s.permitted}</div>
                    <div className="kpi-l">Area terizin</div>
                  </div>
                  <div className="kpi">
                    <div className="kpi-n" style={{ color: "var(--in-progress)" }}>{s.in_progress}</div>
                    <div className="kpi-l">Area dalam proses</div>
                  </div>
                  <div className="kpi">
                    <div className="kpi-n" style={{ color: "var(--blocked)" }}>{s.blocked}</div>
                    <div className="kpi-l">Area ditolak</div>
                  </div>
                  <div className="kpi">
                    <div className="kpi-n">{secPct}%</div>
                    <div className="kpi-l">
                      Persetujuan terpenuhi ({s.granted}/{s.required})
                    </div>
                  </div>
                </div>
                <div className="panel">
                  <h3>Persetujuan per kecamatan</h3>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: "34%" }}>Kecamatan</th>
                        <th style={{ width: "44%" }}>Sebaran status</th>
                        <th style={{ textAlign: "right" }}>Area</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.by_kecamatan.map((k) => (
                        <tr key={k.kecamatan}>
                          <td>{k.kecamatan}</td>
                          <td>
                            <div className="stacked" style={{ width: `${Math.max(14, (k.total / secMax) * 100)}%` }}>
                              <div style={{ width: `${(k.permitted / k.total) * 100}%`, background: "var(--permitted)" }} title={`Diizinkan ${k.permitted}`} />
                              <div style={{ width: `${(k.in_progress / k.total) * 100}%`, background: "var(--in-progress)" }} title={`In Progress ${k.in_progress}`} />
                              <div style={{ width: `${(k.blocked / k.total) * 100}%`, background: "var(--blocked)" }} title={`Ditolak ${k.blocked}`} />
                            </div>
                          </td>
                          <td style={{ textAlign: "right" }} className="mono">
                            {k.total}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {(() => {
              const g = refs?.find((r) => r.work_category === s.work_category);
              if (!g) return null;
              return (
                <WorkCategoryFiles
                  group={g}
                  bare
                  onOpen={onOpen}
                  canOpen={canOpen}
                  canUpload={canUpload}
                  onChanged={() => {
                    void api.references().then(setRefs).catch(() => {});
                  }}
                />
              );
            })()}
          </section>
        );
      })}

      {/* Areas drawn before the work axis existed carry no pair. They count in
          the global totals above, and they read here -- visibly unfiled, never
          silently dropped. */}
      <div className="panel">
        <h3>Tanpa kategori pekerjaan</h3>
        {d.unfiled_work.total > 0 ? (
          <p className="mute small" style={{ margin: 0 }}>
            {d.unfiled_work.total} area belum memiliki kategori pekerjaan (area lama): Diizinkan{" "}
            {d.unfiled_work.permitted}, In Progress {d.unfiled_work.in_progress}, Tertolak{" "}
            {d.unfiled_work.blocked}.
          </p>
        ) : (
          <p className="mute small" style={{ margin: 0 }}>
            Semua area sudah terkelompok -- tanpa kategori pekerjaan: 0.
          </p>
        )}
      </div>
      </>
      )}

      <div className="panel">
        <h3>Aktivitas terakhir</h3>
        {d.recent.length === 0 ? (
          <p className="mute small">Belum ada keputusan tercatat.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Keputusan</th>
                <th>Area</th>
                <th>Dicatat oleh</th>
                <th style={{ textAlign: "right" }}>Waktu</th>
              </tr>
            </thead>
            <tbody>
              {d.recent.map((r) => (
                <tr key={r.decision_id} style={{ cursor: "pointer" }} onClick={() => onOpen(r.area_id)}>
                  <td>
                    <span className={`chip chip-${r.state}`}>
                      <span aria-hidden>{r.state === "granted" ? "✓" : "✕"}</span>{" "}
                      {r.state === "granted" ? "Disetujui" : "Ditolak"}
                    </span>{" "}
                    <span className="small">{r.label}</span>
                  </td>
                  <td className="small">
                    {[r.rt, r.rw, `Kl. ${r.kelurahan}`].filter(Boolean).join(" ")}
                  </td>
                  <td className="small">{r.author}{r.synthetic && <span className="chip"> · Data sintetis</span>}</td>
                  <td style={{ textAlign: "right" }} className="small mute" title={fmtDateTime(r.created_at)}>
                    {relTime(r.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel">
        <h3>Status yang dipakai di peta</h3>
        <div className="stack">
          {(["permitted", "in_progress", "blocked"] as const).map((s) => (
            <div className="rowflex" key={s}>
              <span className={`legend-swatch sw-${s}`} />
              <StatusChip status={s} />
              <span className="small mute">— dibedakan dengan {S[s].patternWord}, bukan hanya warna</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
